<script>
import VueApexCharts from 'vue-apexcharts'

const STATUSES = [
  { key: "AtRisk", label: "At risk", variant: "danger" },
  { key: "Canceling", label: "Canceling", variant: "danger" },
  { key: "TrendingDown", label: "Trending down", variant: "warning" },
  { key: "Growing", label: "Growing", variant: "success" },
  { key: "Steady", label: "Steady", variant: "secondary" },
  { key: "New", label: "New", variant: "info" },
  { key: "Dormant", label: "Dormant", variant: "light" },
];

export default {
  components: {
    VueApexCharts,
  },
  data() {
    return {
      loading: true,
      loadFailed: false,
      accounts: [],
      activeFilter: "NeedsReview",
      searchValue: "",
      currentPage: 1,
      perPage: 25,
      sortBy: "changePercent",
      sortDesc: false,
      fields: [
        { key: "accountName", label: "Account", sortable: true },
        { key: "plan", label: "Plan", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "proofsSentLast30", label: "Proofs (30d)", sortable: true },
        { key: "changePercent", label: "vs Prior 30d", sortable: true },
        { key: "trend", label: "Last 12 Weeks" },
        { key: "lastProofSentAt", label: "Last Proof Sent", sortable: true },
        { key: "lastLoginAt", label: "Last Login", sortable: true },
        { key: "currentPeriodEnd", label: "Renews", sortable: true },
        { key: "impersonate", label: "" },
      ],
      sparklineOptions: {
        chart: { sparkline: { enabled: true }, animations: { enabled: false } },
        stroke: { width: 2, curve: "straight" },
        colors: ["#019256"],
        tooltip: {
          fixed: { enabled: false },
          x: { show: false },
          y: { title: { formatter: () => "Proofs" } },
        },
      },
    };
  },
  async mounted() {
    try {
      this.accounts = await this.$A.SuperAdminService.GetAccountHealth();
    } catch (e) {
      console.log(e);
      this.loadFailed = true;
    }
    this.loading = false;
  },
  computed: {
    tiles: function () {
      let self = this;
      let tiles = [
        {
          key: "NeedsReview",
          label: "Needs review",
          count: this.accounts.filter((a) => a.needsReview).length,
        },
      ];
      STATUSES.forEach(function (status) {
        tiles.push({
          key: status.key,
          label: status.label,
          count: self.accounts.filter((a) => a.status === status.key).length,
        });
      });
      tiles.push({ key: "All", label: "All paying", count: this.accounts.length });
      return tiles;
    },
    filteredAccounts: function () {
      let self = this;
      let search = this.searchValue.trim().toLowerCase();
      return this.accounts.filter(function (account) {
        if (self.activeFilter === "NeedsReview" && !account.needsReview) return false;
        if (
          self.activeFilter !== "NeedsReview" &&
          self.activeFilter !== "All" &&
          account.status !== self.activeFilter
        )
          return false;
        if (search === "") return true;
        return (
          (account.accountName || "").toLowerCase().indexOf(search) !== -1 ||
          (account.ownerEmail || "").toLowerCase().indexOf(search) !== -1 ||
          (account.ownerName || "").toLowerCase().indexOf(search) !== -1 ||
          (account.plan || "").toLowerCase().indexOf(search) !== -1
        );
      });
    },
  },
  methods: {
    LoginAs: async function (account) {
      this.$A.SaveDataCache("admin_impersonate", this.$A.Auth);
      await this.$A.UserService.Login(
          account.ownerEmail,
          "ELCaptain:" + this.$A.Auth.apiToken.token
      );
    },
    setFilter: function (key) {
      this.activeFilter = key;
      this.currentPage = 1;
    },
    statusLabel: function (key) {
      let status = STATUSES.find((s) => s.key === key);
      return status ? status.label : key;
    },
    statusVariant: function (key) {
      let status = STATUSES.find((s) => s.key === key);
      return status ? status.variant : "secondary";
    },
    changeText: function (account) {
      if (account.changePercent === null || account.changePercent === undefined) return "—";
      return (account.changePercent > 0 ? "+" : "") + account.changePercent + "%";
    },
    changeClass: function (account) {
      if (!account.changePercent) return "";
      return account.changePercent > 0 ? "change-up" : "change-down";
    },
    billingCycle: function (account) {
      return account.cycleMonths === 12 ? "Annual" : "Monthly";
    },
    sparklineSeries: function (account) {
      return [{ name: "Proofs", data: account.weeklyProofsSent || [] }];
    },
  },
  watch: {
    searchValue: function () {
      this.currentPage = 1;
    },
  },
};
</script>
<template>
  <div>
    <navigation/>
    <div class="container-fluid mt-50">
      <b-card class="customers-card" style="margin-top: 100px">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h4 class="mb-1">Account Health</h4>
            <div class="text-muted">
              Paying accounts, by proofs sent in the last 30 days against the 30 days before.
              <a href="/captains-deck" class="view-link">Customer list</a>
            </div>
          </div>
          <b-form-input
              v-model="searchValue"
              class="health-search"
              placeholder="Search account, owner or plan"
          />
        </div>

        <b-alert variant="danger" :show="loadFailed">
          Account health could not be loaded. Refresh to try again.
        </b-alert>

        <div v-if="loading" class="text-center py-5">
          <b-spinner label="Loading"/>
        </div>

        <div v-else-if="!loadFailed">
          <div class="health-tiles mb-3">
            <a
                v-for="tile in tiles"
                :key="tile.key"
                class="health-tile"
                :class="{ active: activeFilter === tile.key }"
                @click="setFilter(tile.key)"
            >
              <div class="health-tile-count">{{ tile.count }}</div>
              <div class="health-tile-label">{{ tile.label }}</div>
            </a>
          </div>

          <div v-if="!filteredAccounts.length" class="text-center text-muted py-5">
            No accounts match this view.
          </div>

          <b-table v-else id="tableAccountHealth"
                   class="rounded"
                   :items="filteredAccounts"
                   :fields="fields"
                   :per-page="perPage"
                   :current-page="currentPage"
                   :sort-by.sync="sortBy"
                   :sort-desc.sync="sortDesc"
                   sort-null-last
                   primary-key="accountId"
                   responsive outlined bordered borderless
                   thead-class="table-bg"
                   tbody-class="table-body"
          >
            <template #head()="data">
              <span class="table-header-text">{{ data.label }}</span>
            </template>
            <template #cell(accountName)="data">
              <div>{{ data.item.accountName }} <span class="text-muted">#{{ data.item.accountId }}</span></div>
              <div class="text-muted small">
                {{ data.item.ownerEmail }} · {{ data.item.userCount }} {{ data.item.userCount === 1 ? "user" : "users" }}
              </div>
            </template>
            <template #cell(plan)="data">
              <div>{{ data.item.plan }}</div>
              <div class="text-muted small">
                {{ billingCycle(data.item) }}<span v-if="data.item.resellerId > 0"> · via reseller</span>
              </div>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">{{ statusLabel(data.item.status) }}</b-badge>
              <div class="text-muted small">{{ data.item.reason }}</div>
            </template>
            <template #cell(proofsSentLast30)="data">
              {{ data.item.proofsSentLast30 }}
              <span class="text-muted small">/ {{ data.item.proofsSentPrev30 }} prior</span>
            </template>
            <template #cell(changePercent)="data">
              <span :class="changeClass(data.item)">{{ changeText(data.item) }}</span>
            </template>
            <template #cell(trend)="data">
              <VueApexCharts
                  type="line"
                  height="32"
                  width="120"
                  :options="sparklineOptions"
                  :series="sparklineSeries(data.item)"
              />
            </template>
            <template #cell(lastProofSentAt)="data">
              {{ $A.FormatDateToString(data.item.lastProofSentAt, true) }}
            </template>
            <template #cell(lastLoginAt)="data">
              {{ $A.FormatDateToString(data.item.lastLoginAt, true) }}
            </template>
            <template #cell(currentPeriodEnd)="data">
              {{ $A.FormatDateToString(data.item.currentPeriodEnd, true) }}
            </template>
            <template #cell(impersonate)="data">
              <a :href="'/captains-deck/review/' + data.item.accountId" target="_blank" class="view-link">Start review</a>
              <span class="text-muted mx-1">·</span>
              <a v-if="data.item.ownerEmail" @click="LoginAs(data.item)" class="view-link">Impersonate</a>
            </template>
          </b-table>

          <b-pagination
              v-if="filteredAccounts.length > perPage"
              v-model="currentPage"
              :per-page="perPage"
              :total-rows="filteredAccounts.length"
              aria-controls="tableAccountHealth"
              align="right"
              size="md"
          />
        </div>
      </b-card>
    </div>
  </div>
</template>

<style scoped>

.container-fluid {
  max-width: 2000px !important;
}

.health-search {
  max-width: 320px;
}

.health-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.health-tile {
  min-width: 120px;
  padding: 12px 16px;
  border: 1px solid #EBEBED;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  text-decoration: none;
}

.health-tile.active {
  border-color: #019256;
  background: #F4F4F6;
}

.health-tile-count {
  font-size: 24px;
  line-height: 28px;
  font-weight: 600;
  color: #1B1B1C;
}

.health-tile-label {
  font-size: 13px;
  color: #6c757d;
}

.change-up {
  color: #019256;
}

.change-down {
  color: #dc3545;
}

.view-link {
  color: #019256 !important;
  cursor: pointer;
}

::v-deep .table-body {
  background-color: white !important;
}

::v-deep .table-bg {
  background: #F4F4F6;
  border-bottom: 0.2px solid #EBEBED;
}

.table-header-text {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #1B1B1C;
}

::v-deep .table td {
  vertical-align: middle;
}

::v-deep .page-item .page-link {
  color: #019256 !important;
}

::v-deep .page-item.disabled .page-link {
  color: #6c757d !important;
}

::v-deep .customers-card .card-body {
  padding: 24px !important;
}

@media screen and (max-width: 998px) {

  ::v-deep .customers-card {
    margin-top: 0px !important;
  }
}

</style>
